import { clsx } from 'clsx';
import { VENDOR_FIELDS } from '@/types/onboarding';

interface VendorFieldsStepProps {
  selectedFields: string[];
  onChange: (fields: string[]) => void;
}

export const VendorFieldsStep = ({ selectedFields, onChange }: VendorFieldsStepProps) => {
  const handleToggle = (key: string) => {
    const field = VENDOR_FIELDS.find((f) => f.key === key);
    if (field?.alwaysOn) return;
    if (selectedFields.includes(key)) {
      onChange(selectedFields.filter((f) => f !== key));
    } else {
      onChange([...selectedFields, key]);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Vendor Fields</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Choose which fields to auto-populate when creating vendors from extracted document data.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {VENDOR_FIELDS.map((field) => {
          const isAlwaysOn = field.alwaysOn === true;
          const isChecked = isAlwaysOn || selectedFields.includes(field.key);

          return (
            <label
              key={field.key}
              className={clsx(
                'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                isAlwaysOn && 'cursor-default',
                isChecked
                  ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20'
                  : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
              )}
            >
              <input
                type="checkbox"
                checked={isChecked}
                disabled={isAlwaysOn}
                onChange={() => handleToggle(field.key)}
                className={clsx(
                  'mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500',
                  isAlwaysOn && 'opacity-50'
                )}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {field.label}
                  </span>
                  {isAlwaysOn && (
                    <span className="text-xs text-gray-400">Required</span>
                  )}
                </div>
                {field.description && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {field.description}
                  </p>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};
